import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toast } from 'react-toastify'; // <-- Importamos react-toastify
import { ApiService } from './ApiService';
import { useNavigate } from 'react-router-dom';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
};

export const ModalRegistro = ({
  open,
  onClose,
  userData,
  validationMethod,
  onRegisterComplete
}) => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [loadingReg, setLoadingReg] = useState(true);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setCode(['', '', '', '', '', '']);
      setLoadingReg(true);
      registerUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Paso 1: POST /register => si OK => POST /sendCode => Step 2
  const registerUser = async () => {
    try {
      const response = await ApiService.fetchData('api/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
  
      if (!response) {
        // toast.error(response.message);
        setLoadingReg(false);
        onClose();
        return;
      }
  
      const errorObj = Array.isArray(response) ? response[0] : response;


      if (errorObj.code === 400) {
        toast.error(errorObj?.message??"Error, por favor intentalo nuevamente mas tarde");
        setLoadingReg(false);
        onClose();
        return;
        
      }
      // } else {
      //   toast.error(errorObj.message || 'Error en el registro');
      // }
     

      const codeOk = await sendCode();
      if (!codeOk) {
        throw new Error('Error al enviar el código');
      }
  
      setLoadingReg(false);
      setStep(2);
  
    } catch (error) {
      setLoadingReg(false);
      onClose();
      toast.error(error.message || 'Error inesperado');
    }
  };
  ;

  // Paso 2: POST /sendCode
  const sendCode = async () => {
    try {
      const body_send = {};
      if (validationMethod === 'email') {
        body_send.email_User = userData.emailUser;
      } else {
        body_send.full_phone = userData.fullphone;
      }

      const response = await ApiService.fetchData('api/auth/sendCode', {
        method: 'POST',
        body: JSON.stringify(body_send),
      });

      if (!response || response.code !== 200) {
        throw new Error(response?.message || 'Error al enviar el código');
      }

      return true;
    } catch (error) {
      toast.error(error.message || 'Error al enviar código');
      return false;
    }
  };

  // Manejo de inputs de 6 dígitos
  const handleDigitChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index < 5) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    const key = e.key;
    if (key === 'Backspace') {
      if (code[index]) {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const newCode = [...code];
        newCode[index - 1] = '';
        setCode(newCode);
      }
    } else if (key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('Text');
    if (/^\d{6}$/.test(pasted)) {
      const arr = pasted.split('');
      setCode(arr);
      inputsRef.current[5]?.focus();
    }
  };

  // Paso 2: Botón Verificar => POST /verifyCode => Step 3
  const handleVerify = async () => {
    setVerifying(true);
    try {
      const digits = code.join('');
      if (digits.length < 6) {
        throw new Error('Completa los 6 dígitos');
      }
      const body_send = { code: digits };
      if (validationMethod === 'email') {
        body_send.email_User = userData.emailUser;
      } else {
        body_send.full_phone = userData.fullphone;
      }

      const response = await ApiService.fetchData('api/auth/verifyCode', {
        method: 'POST',
        body: JSON.stringify(body_send),
      });

      if (!response) {
        throw new Error('Error al verificar código');
      }

      // Verificación exitosa => Step 3
      setStep(3);

    } catch (error) {
      toast.error(error.message || 'Error al verificar código');
    } finally {
      setVerifying(false);
    }
  };

  // Paso 3: pulsar Aceptar => onClose() + onRegisterComplete()
  const handleAcceptSuccess = () => {
    onClose();
    onRegisterComplete(); // Cambia a Login en AuthPage
  };

  return (
    <Modal
      open={open}
      // Evitar que se cierre por clic fuera / ESC
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      disableEscapeKeyDown
      hideBackdrop={false}
      BackdropProps={{
        style: { pointerEvents: 'none' }
      }}
    >
      <Box sx={style}>
        {/* Encabezado dinámico */}
        <Box mb={2} sx={{ bgcolor: '#d32f2f', p: 1.5 }}>
          <Typography variant="h6" sx={{ color: 'white', m: 0 }}>
            {step === 1
              ? 'Registrando'
              : step === 2
              ? `Verificación de ${validationMethod === 'email' ? 'Correo' : 'Teléfono'}`
              : 'Registro Exitoso'}
          </Typography>
        </Box>

        {/* Paso 1: Registrando... */}
        {step === 1 && (
          <Box textAlign="center" px={2} pb={2}>
            <Typography variant="body2" gutterBottom sx={{ fontSize: '16px' }}>
              Por favor no cierres esta página
            </Typography>
            <Box
              mt={2}
              display="flex"
              justifyContent="center"
              sx={{ marginTop: 3, marginBottom: 3 }}
            >
              {loadingReg && <CircularProgress />}
            </Box>
          </Box>
        )}

        {/* Paso 2: Ingresar los 6 dígitos */}
        {step === 2 && (
          <>
            <Typography
              variant="body2"
              gutterBottom
              style={{ textAlign: 'center', fontSize: '16px' }}
            >
              Coloca los 6 dígitos enviados a tu{' '}
              {validationMethod === 'email' ? 'Correo' : 'Teléfono'}
            </Typography>

            <Box
              display="flex"
              gap={1}
              justifyContent="center"
              mt={2}
              onPaste={handlePaste}
            >
              {code.map((digit, idx) => (
                <TextField
                  key={idx}
                  value={digit}
                  inputRef={(el) => (inputsRef.current[idx] = el)}
                  onChange={(e) => handleDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                  sx={{ width: 40 }}
                />
              ))}
            </Box>

            <Box mt={3} textAlign="center" sx={{ paddingBottom: 3 }}>
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={verifying}
                sx={{ backgroundColor: '#d32f2f' }}
              >
                {verifying ? 'Cargando...' : 'Verificar'}
              </Button>
            </Box>
          </>
        )}

        {/* Paso 3: Registro Exitoso */}
        {step === 3 && (
          <Box textAlign="center" px={2} pb={2}>
            <Box display="flex" justifyContent="center" mb={1}>
              <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 48 }} />
            </Box>
            <Typography variant="h6" sx={{ fontSize: '18px', mb: 1 }}>
              ¡Registro Exitoso!
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '16px', mb: 4 }}>
              Inicie sesión con sus datos
            </Typography>

            <Box textAlign="center">
              <Button
                variant="contained"
                sx={{ backgroundColor: '#d32f2f' }}
                onClick={handleAcceptSuccess}
              >
                Aceptar
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};
